import React from 'react';

function SuccessView({ onContinue, title = 'Created Successfully', message, continueLabel = 'Continue' }) {
  return (
    <div className="flex flex-col items-center text-center px-6 pb-8 pt-4">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full border-4 border-green-100 bg-green-50">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h4 className="mb-2 text-[17px] font-bold text-gray-900">{title}</h4>
      <p className="mb-7 text-sm leading-relaxed text-gray-500">
        {message ?? (
          <>
            The details have been saved and are now available in the system. Select <strong>{continueLabel}</strong> to close this modal.
          </>
        )}
      </p>
      <button
        onClick={onContinue}
        className="h-12 w-full rounded-full bg-green-500 text-sm font-semibold text-white transition hover:bg-green-600"
      >
        {continueLabel}
      </button>
    </div>
  );
}

function ErrorView({ message, onClose }) {
  return (
    <div className="flex flex-col items-center px-6 pb-8 pt-4 text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full border-4 border-red-100 bg-red-50">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>
      <h4 className="mb-2 text-[17px] font-bold text-gray-900">Something went wrong...</h4>
      <p className="mb-7 text-sm leading-relaxed text-gray-500">{message}</p>
      <div className="flex w-full gap-3">
        <button className="flex-1 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 h-12">
          Contact Support
        </button>
        <button onClick={onClose} className="flex-1 h-12 rounded-full bg-green-500 text-sm font-semibold text-white transition hover:bg-green-600">
          Close
        </button>
      </div>
    </div>
  );
}

export default function Modal({
  title,
  isOpen,
  onClose,
  onSubmit,
  children,
  status = 'idle',
  errorMessage = '',
  onContinue,
  submitLabel = 'Create',
  successTitle,
  successMessage,
  successContinueLabel = 'Continue',
  isSubmitting = false,
  maxWidthClassName = 'max-w-[540px]',
  bodyGridClassName = 'grid gap-5',
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-gray-900/45 px-4 py-10">
      <div
        className={`flex max-h-[90vh] w-full flex-col rounded-2xl bg-white shadow-2xl ${maxWidthClassName}`}
      >
        <div className="flex shrink-0 items-center justify-between px-6 py-5">
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={isSubmitting ? undefined : onClose}
            aria-label="Close"
            disabled={isSubmitting}
            className="flex h-7 w-7 items-center justify-center rounded-full text-xl leading-none text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            x
          </button>
        </div>

        {status === 'success' ? (
          <SuccessView
            onContinue={onContinue ?? onClose}
            title={successTitle}
            message={successMessage}
            continueLabel={successContinueLabel}
          />
        ) : status === 'error' ? (
          <ErrorView message={errorMessage} onClose={onClose} />
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 pb-4">
              <div className="rounded-2xl border border-gray-100 bg-white p-5">
                <div className={bodyGridClassName}>{children}</div>
              </div>
            </div>

            <div className="flex shrink-0 gap-3 px-6 pb-6 pt-4">
              <button
                type="button"
                onClick={isSubmitting ? undefined : onClose}
                disabled={isSubmitting}
                className="flex-1 h-12 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Close
              </button>
              <button
                type="button"
                onClick={onSubmit}
                disabled={isSubmitting}
                className="flex-1 h-12 rounded-full bg-green-500 text-sm font-semibold text-white transition hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? 'Saving...' : submitLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
